import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TrackerDataType } from '../../shared/constants/constants';
import { getForwardingAddress } from '../AutoTrackerPage/emailScrapingRequests';
import {
  trackerToolbarButtonTypographySx,
  trackerToolbarNeutralHoverFill,
  trackerToolbarNeutralOutlineColor,
  trackerToolbarNeutralTextColor,
} from '../TrackerFilter/trackerFilterToolbarStyles';
import { appPalette, neutralPalette } from '../../theme/palette';
import MadeByAutoTrackerIcon from '../TrackerFilter/Icons/MadeByAutoTrackerIcon';
import CloseIcon from '@mui/icons-material/Close';
import {
  utilityModalDialogSx,
  utilityModalPaperSx,
  utilityModalHeaderSx,
  utilityModalContentSx,
  utilityModalIconTileSx,
  utilityModalEyebrowSx,
  utilityModalSubtitleSx,
  adderHeaderTitleSx,
  utilitySecondaryActionButtonSx,
} from '../QueryAdder/queryAdderPopupStyles';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { fontFamilies } from '../../theme/typography';

interface AddAutomaticallyButtonProps {
  eventHandler: any;
}
const AddAutomaticallyButton = (props: AddAutomaticallyButtonProps) => {
  const { eventHandler } = props;
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [forwardingAddress, setForwardingAddress] = useState('');
  const trackerType = (Object.values(TrackerDataType) as string[]).includes(eventHandler?.type)
    ? (eventHandler.type as TrackerDataType)
    : TrackerDataType.SERVICE;

  const handleClose = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    getForwardingAddress().then((response) => {
      setForwardingAddress(response);
    });
  }, []);

  const serviceAutoTrackerConfig = {
    titleText: 'Automatically Add a Service',
    helperText:
      'Forward a relevant email to the email address below. AutoTracker will prepare a draft Service in about 30 seconds and add it to your Service Log.',
    emailName: forwardingAddress ? forwardingAddress.replace('interaction', 'service') : '',
  };

  const getAutoTrackerConfig = () => {
    switch (trackerType) {
      case TrackerDataType.MEDIA_INTERACTION:
        return {
          titleText: 'Automatically Add an Interaction',
          helperText:
            'Forward a relevant email to the email address below. AutoTracker will prepare a draft Media Interaction in about 30 seconds and add it to your Interactions Log.',
          emailName: forwardingAddress || '',
        };
      case TrackerDataType.SERVICE:
        return serviceAutoTrackerConfig;
      case TrackerDataType.ENGAGEMENT:
        return {
          titleText: 'Automatically Add an Engagement',
          helperText:
            'Forward a relevant email to the email address below. AutoTracker will prepare a draft Engagement in about 30 seconds and add it to your Engagement Log.',
          emailName: forwardingAddress ? forwardingAddress.replace('interaction', 'engagement') : '',
        };
      case TrackerDataType.CONTENT_LOG:
        return {
          titleText: 'Automatically Add Content',
          helperText:
            'Forward a relevant email to the email address below. AutoTracker will prepare a draft Content item in about 30 seconds and add it to your Content Log.',
          emailName: forwardingAddress ? forwardingAddress.replace('interaction', 'content') : '',
        };
      default:
        return serviceAutoTrackerConfig;
    }
  };

  const { titleText, helperText, emailName } = getAutoTrackerConfig();

  const buttonOutlineColor = trackerToolbarNeutralOutlineColor;
  const buttonLabelColor = trackerToolbarNeutralTextColor;
  const buttonSoftFill = 'transparent';
  const buttonSoftFillHover = trackerToolbarNeutralHoverFill;
  const copyForwardingAddress = async () => {
    if (!emailName) {
      dispatch(openSnackbar({ severity: 'warning', message: 'No forwarding address available to copy.' }));
      return;
    }
    try {
      await navigator.clipboard.writeText(emailName);
      dispatch(openSnackbar({ severity: 'success', message: 'Forwarding address copied.' }));
    } catch (error) {
      dispatch(openSnackbar({ severity: 'error', message: 'Unable to copy forwarding address.' }));
    }
  };

  const autoTrackerTitleSx = {
    ...adderHeaderTitleSx,
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 21,
    fontWeight: 550,
    lineHeight: 1.3,
    color: '#202124',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    maxWidth: '100%',
  } as const;

  const subtleUtilityIconButtonSx = {
    width: 32,
    height: 32,
    p: 0,
    border: `1px solid ${neutralPalette.borderSubtle}`,
    borderRadius: '8px',
    color: neutralPalette.textSecondary,
    backgroundColor: neutralPalette.white,
    '&:hover': {
      backgroundColor: neutralPalette.surfacePage,
      borderColor: neutralPalette.borderMuted,
      color: neutralPalette.textPrimary,
    },
  } as const;

  const copyEmailButtonSx = {
    ...utilitySecondaryActionButtonSx,
    minWidth: 120,
    height: 34,
    px: 1.5,
    borderRadius: '8px',
    fontSize: 14,
    fontWeight: 500,
    borderColor: 'rgba(15, 23, 42, 0.14)',
    color: neutralPalette.textPrimary,
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(21, 91, 181, 0.06)',
      borderColor: 'rgba(21, 91, 181, 0.28)',
    },
  } as const;

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          height: '32px',
          color: buttonLabelColor,
          borderColor: buttonOutlineColor,
          backgroundColor: buttonSoftFill,
          '&:hover': {
            borderColor: buttonOutlineColor,
            backgroundColor: buttonSoftFillHover,
          },
          '& .MuiButton-startIcon .MuiSvgIcon-root': {
            fontSize: 18,
          },
          ...trackerToolbarButtonTypographySx,
        }}
        startIcon={<MadeByAutoTrackerIcon sx={{ color: appPalette.primary.main }} />}
        onClick={() => setOpenModal(true)}
        data-cy="addAutomatically"
      >
        AutoTracker
      </Button>
      <Dialog
        open={openModal}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={utilityModalDialogSx}
        PaperProps={{
          sx: {
            ...utilityModalPaperSx,
            width: 560,
            maxWidth: 'calc(100vw - 48px)',
            borderRadius: 5,
            backgroundColor: '#fff',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.22)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            ...utilityModalHeaderSx,
            position: 'relative',
            px: 3,
            pt: 3,
            pb: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            pr: 7,
            borderBottom: 'none',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={utilityModalIconTileSx}>
              <MadeByAutoTrackerIcon sx={{ color: appPalette.primary.main, fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={utilityModalEyebrowSx}>AUTOTRACKER</Typography>
              <Typography sx={autoTrackerTitleSx}>{titleText}</Typography>
              <Typography
                sx={{
                  ...utilityModalSubtitleSx,
                  mt: 1.25,
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: '#5F6368',
                  maxWidth: '100%',
                }}
              >
                {helperText}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Close AutoTracker dialog"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 24,
              right: 16,
              ...subtleUtilityIconButtonSx,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            ...utilityModalContentSx,
            pt: 3,
            pb: 4,
          }}
        >
          <Box
            component={Paper}
            variant="outlined"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              ml: { xs: 0, sm: '64px' },
              mr: { xs: 0, sm: 4 },
              mt: 0.5,
              px: 1.75,
              py: 1.15,
              borderRadius: '5px',
              border: '1px solid rgba(15, 23, 42, 0.14)',
              boxShadow: 'none',
              backgroundColor: '#fff',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.2 }}>
              <Typography
                sx={{
                  ...utilityModalEyebrowSx,
                  lineHeight: 1,
                  mb: 0,
                }}
              >
                FORWARD TO
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                sx={{
                  fontFamily: fontFamilies.body,
                  fontSize: 15,
                  fontWeight: 400,
                  lineHeight: 1,
                  color: neutralPalette.textPrimary,
                  overflowWrap: 'anywhere',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {emailName}
              </Typography>
              <Button
                onClick={copyForwardingAddress}
                variant="outlined"
                startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...copyEmailButtonSx,
                  ml: 'auto',
                  flexShrink: 0,
                }}
                aria-label="Copy forwarding address"
              >
                Copy address
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddAutomaticallyButton;
